import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface RevenueData {
  current: number;
  target: number;
  percentage: number;
}

@Component({
  selector: 'app-estimated-revenue',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-900 font-outfit mb-6">{{ title }}</h3>
      
      <!-- Radial Progress Chart -->
      <div class="flex items-center justify-center mb-6">
        <div class="relative w-32 h-32">
          <!-- Background circle -->
          <svg class="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="50"
              stroke-width="8"
              stroke="#f3f4f6"
              fill="transparent"
              class="drop-shadow-sm">
            </circle>
            <!-- Progress circle -->
            <circle
              cx="60"
              cy="60"
              r="50"
              stroke-width="8"
              [attr.stroke]="progressColor"
              fill="transparent"
              stroke-linecap="round"
              [style.stroke-dasharray]="circumference"
              [style.stroke-dashoffset]="dashOffset"
              class="transition-all duration-500 ease-out drop-shadow-sm">
            </circle>
          </svg>
          <!-- Percentage text in center -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="text-center">
              <span class="text-2xl font-bold text-gray-900">{{ revenueData.percentage }}%</span>
              <div class="text-xs text-gray-500 mt-1">Complete</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Revenue details -->
      <div class="text-center space-y-3">
        <div>
          <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(revenueData.current) }}</p>
          <p class="text-sm text-gray-600">Current Revenue</p>
        </div>
        
        <div class="pt-3 border-t border-gray-100">
          <p class="text-lg font-semibold text-gray-700">{{ formatCurrency(revenueData.target) }}</p>
          <p class="text-sm text-gray-500">Target Revenue</p>
        </div>
        
        <div class="pt-3 border-t border-gray-100">
          <p class="text-sm font-medium text-gray-600">
            {{ formatCurrency(revenueData.target - revenueData.current) }} remaining
          </p>
          <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              class="h-2 rounded-full transition-all duration-500 ease-out"
              [class]="progressBarColor"
              [style.width.%]="revenueData.percentage">
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``
})
export class EstimatedRevenueComponent {
  @Input() title: string = 'Estimated Revenue';
  @Input() revenueData: RevenueData = {
    current: 55000,
    target: 75000,
    percentage: 73
  };

  readonly circumference = 2 * Math.PI * 50; // 2πr where r=50

  get dashOffset(): number {
    return this.circumference - (this.revenueData.percentage / 100) * this.circumference;
  }

  get progressColor(): string {
    if (this.revenueData.percentage >= 80) return '#10b981'; // success-500
    if (this.revenueData.percentage >= 60) return '#b55299'; // brand-500
    if (this.revenueData.percentage >= 40) return '#f59e0b'; // warning-500
    return '#ef4444'; // error-500
  }

  get progressBarColor(): string {
    if (this.revenueData.percentage >= 80) return 'bg-success-500';
    if (this.revenueData.percentage >= 60) return 'bg-brand-500';
    if (this.revenueData.percentage >= 40) return 'bg-warning-500';
    return 'bg-error-500';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}